import {
   collection,
   query,
   where,
   getDocs,
   addDoc,
   updateDoc,
   deleteDoc,
   doc,
   getDoc,
   serverTimestamp,
   Timestamp,
   arrayUnion,
   arrayRemove,
   writeBatch,
   orderBy,
} from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { TripParticipant, TripRole, ParticipantStatus } from '../types/Trip';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import { getAuthHeaders } from '../utils/auth';
import axios from 'axios';

/**
 * Get all participants for a specific trip
 */
export const getTripParticipants = async (tripId: string): Promise<TripParticipant[]> => {
   try {
      // Use backend API instead of direct Firestore access for authentication
      const headers = await getAuthHeaders();

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/trips/${tripId}`, {
         method: 'GET',
         headers,
      });

      if (!response.ok) {
         throw new Error(`Failed to fetch trip: ${response.status}`);
      }

      const result = await response.json();
      const tripData = result.data;

      // Ensure tripData is defined
      if (!tripData) {
         console.error('Trip data is undefined');
         return [];
      }

      // Use only the new members array structure from backend API
      const allParticipants = Array.isArray(tripData.members) ? tripData.members : [];

      if (allParticipants.length === 0) {
         console.warn('No members found in trip data', { tripId, tripData });
         return [];
      }

      // Convert members to TripParticipant format
      const participants: TripParticipant[] = allParticipants.map((member: any) => ({
         userId: member.userId || '',
         email: member.email || '',
         displayName: member.displayName || member.name || 'Unknown User',
         photoURL: member.photoURL || '',
         role: member.role || 'viewer',
         status: 'accepted', // Members in the members array are already accepted
         invitedAt: member.joinedAt || tripData.createdAt || new Date().toISOString(),
         invitedBy: tripData.createdBy || '',
         acceptedAt: member.joinedAt || tripData.createdAt || new Date().toISOString(),
      }));

      console.log('Processed trip participants:', participants);
      return participants;
   } catch (error) {
      console.error('Error getting trip participants:', error);
      throw error;
   }
};

/**
 * Check if a user has access to a trip
 */
export const checkTripAccess = async (
   tripId: string,
   userId: string
): Promise<{ hasAccess: boolean; role?: TripRole; isOwner: boolean }> => {
   try {
      const tripDoc = await getDoc(doc(db, 'trips', tripId));

      if (!tripDoc.exists()) {
         return { hasAccess: false, isOwner: false };
      }

      const tripData = tripDoc.data();

      // If user is the trip owner, they have full access
      if (tripData.userId === userId) {
         return { hasAccess: true, role: 'owner', isOwner: true };
      }

      // Check participants array
      const participants = tripData.participants || [];
      const userParticipant = participants.find(
         (p: TripParticipant) => p.userId === userId && p.status === 'accepted'
      );

      if (userParticipant) {
         return {
            hasAccess: true,
            role: userParticipant.role as TripRole,
            isOwner: false,
         };
      }

      return { hasAccess: false, isOwner: false };
   } catch (error) {
      console.error('Error checking trip access:', error);
      throw error;
   }
};

/**
 * Invite a user to a trip
 */
export const inviteUserToTrip = async (
   tripId: string,
   email: string,
   role: TripRole,
   inviterId: string
): Promise<void> => {
   try {
      // First, check if the trip exists
      const tripDoc = await getDoc(doc(db, 'trips', tripId));

      if (!tripDoc.exists()) {
         throw new Error('Trip not found');
      }

      const tripData = tripDoc.data();

      // Get members array and check if inviter has permission (owner or editor)
      const members = Array.isArray(tripData.members) ? tripData.members : [];
      const inviterMember = members.find((member) => member.userId === inviterId);

      if (!inviterMember) {
         console.error('Inviter not found in trip members array', {
            tripId,
            inviterId,
            members: members,
         });
         throw new Error('You must be a member of this trip to send invitations');
      }

      // Allow both owners and editors to invite users
      const canInvite = inviterMember.role === 'owner' || inviterMember.role === 'editor';

      if (!canInvite) {
         throw new Error('Only trip owners and editors can invite users');
      }

      // Use the provided role (default to 'editor' if not specified)
      if (!role) {
         role = 'editor';
      }

      // Check if user already exists in the system
      const userQuery = query(collection(db, 'users'), where('email', '==', email.toLowerCase()));

      const userQuerySnapshot = await getDocs(userQuery);
      let userId = '';
      let displayName = '';
      let photoURL = '';

      if (!userQuerySnapshot.empty) {
         // User exists
         const userData = userQuerySnapshot.docs[0].data();
         userId = userQuerySnapshot.docs[0].id;
         displayName = userData.displayName || email.split('@')[0];
         photoURL = userData.photoURL || '';
      } else {
         // User doesn't exist yet, we'll use email as identifier
         displayName = email.split('@')[0];
      }

      // Check if user is already a participant
      const existingParticipant = members.find(
         (p: TripParticipant) => p.email.toLowerCase() === email.toLowerCase()
      );

      if (existingParticipant) {
         throw new Error('User is already invited to this trip');
      }

      // Create the invitation record
      const invitationData = {
         tripId,
         email: email.toLowerCase(),
         role,
         status: 'pending',
         inviterId,
         invitedAt: serverTimestamp(),
         updatedAt: serverTimestamp(),
         expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days expiry
      };

      // Save the invitation to Firestore
      const inviteRef = await addDoc(collection(db, 'tripInvitations'), invitationData);

      // Add user to the trip's participants array
      const newParticipant = {
         userId,
         email: email.toLowerCase(),
         displayName,
         photoURL,
         role,
         status: 'pending',
         invitedAt: new Date().toISOString(),
         invitedBy: inviterId,
         invitationId: inviteRef.id,
      };

      // Update the trip document with the new participant
      await updateDoc(doc(db, 'trips', tripId), {
         participants: arrayUnion(newParticipant),
         updatedAt: serverTimestamp(),
      });

      // In a real application, you would send an email to the invitee here
   } catch (error) {
      console.error('Error inviting user to trip:', error);
      throw error;
   }
};

/**
 * Accept a trip invitation
 */
export const acceptTripInvitation = async (tripId: string, userId: string): Promise<void> => {
   try {
      const tripDoc = await getDoc(doc(db, 'trips', tripId));

      if (!tripDoc.exists()) {
         throw new Error('Trip not found');
      }

      const tripData = tripDoc.data();
      const participants = tripData.participants || [];

      // Find the user's invitation
      const userIndex = participants.findIndex((p: TripParticipant) => p.userId === userId);

      if (userIndex === -1) {
         throw new Error('Invitation not found');
      }

      // Update the participant's status
      participants[userIndex].status = 'accepted';
      participants[userIndex].acceptedAt = new Date().toISOString();

      // Update the trip document
      await updateDoc(doc(db, 'trips', tripId), {
         participants,
      });
   } catch (error) {
      console.error('Error accepting trip invitation:', error);
      throw error;
   }
};

/**
 * Remove a user from a trip and delete their photos
 */
export const removeUserFromTrip = async (tripId: string, userIdToRemove: string): Promise<void> => {
   try {
      const tripDoc = await getDoc(doc(db, 'trips', tripId));

      if (!tripDoc.exists()) {
         throw new Error('Trip not found');
      }

      const tripData = tripDoc.data();

      // Prevent removing the trip owner
      if (tripData.userId === userIdToRemove) {
         throw new Error('Cannot remove the trip owner');
      }

      const participants = tripData.participants || [];

      // Find the user in participants
      const userParticipant = participants.find(
         (p: TripParticipant) => p.userId === userIdToRemove
      );

      if (!userParticipant) {
         throw new Error('User is not a participant of this trip');
      }

      // Use a batch to handle multiple operations
      const batch = writeBatch(db);

      // 1. Remove user from participants array
      const updatedParticipants = participants.filter(
         (p: TripParticipant) => p.userId !== userIdToRemove
      );
      batch.update(doc(db, 'trips', tripId), {
         participants: updatedParticipants,
      });

      // 2. Delete user's photos for this trip
      const photosQuery = query(
         collection(db, 'photos'),
         where('tripId', '==', tripId),
         where('userId', '==', userIdToRemove)
      );

      const photosQuerySnapshot = await getDocs(photosQuery);

      photosQuerySnapshot.forEach((photoDoc) => {
         batch.delete(photoDoc.ref);
      });

      // Commit the batch
      await batch.commit();
   } catch (error) {
      console.error('Error removing user from trip:', error);
      throw error;
   }
};

/**
 * Change a participant's role
 */
export const changeParticipantRole = async (
   tripId: string,
   participantUserId: string,
   newRole: TripRole
): Promise<void> => {
   try {
      const tripDoc = await getDoc(doc(db, 'trips', tripId));

      if (!tripDoc.exists()) {
         throw new Error('Trip not found');
      }

      const tripData = tripDoc.data();

      // Prevent changing the owner's role
      if (tripData.userId === participantUserId) {
         throw new Error("Cannot change the owner's role");
      }

      const participants = tripData.participants || [];

      // Find the participant
      const participantIndex = participants.findIndex(
         (p: TripParticipant) => p.userId === participantUserId
      );

      if (participantIndex === -1) {
         throw new Error('Participant not found');
      }

      // Update the participant's role
      participants[participantIndex].role = newRole;

      // Update the trip document
      await updateDoc(doc(db, 'trips', tripId), {
         participants,
      });
   } catch (error) {
      console.error('Error changing participant role:', error);
      throw error;
   }
};

/**
 * Get a user's pending trip invitations
 */
export const getUserPendingInvitations = async (userEmail: string): Promise<any[]> => {
   try {
      // Query trips where the user's email is in participants with status pending
      const tripsQuery = query(collection(db, 'trips'));

      const tripsSnapshot = await getDocs(tripsQuery);
      const pendingInvitations: any[] = [];

      tripsSnapshot.forEach((tripDoc) => {
         const tripData = tripDoc.data();
         const participants = tripData.participants || [];

         const userInvitation = participants.find(
            (p: TripParticipant) =>
               p.email.toLowerCase() === userEmail.toLowerCase() && p.status === 'pending'
         );

         if (userInvitation) {
            pendingInvitations.push({
               tripId: tripDoc.id,
               tripTitle: tripData.title,
               startDate: tripData.startDate,
               endDate: tripData.endDate,
               coverPhotoUrl: tripData.coverPhotoUrl,
               invitedBy: userInvitation.invitedBy,
               invitedAt: userInvitation.invitedAt,
               role: userInvitation.role,
            });
         }
      });

      return pendingInvitations;
   } catch (error) {
      console.error('Error getting user pending invitations:', error);
      throw error;
   }
};

/**
 * Resend an invitation to a pending participant
 */
export const resendInvitation = async (invitationId: string): Promise<void> => {
   try {
      const invitationSnapshot = await getDoc(doc(db, 'tripInvitations', invitationId));

      if (!invitationSnapshot.exists()) {
         throw new Error('Invitation not found');
      }
      const invitation = invitationSnapshot.data();

      if (invitation.status === 'accepted') {
         throw new Error('Invitation has already been accepted');
      }

      // Update the invitation with new expiry date
      await updateDoc(doc(db, 'tripInvitations', invitationId), {
         expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
         updatedAt: new Date(),
      });

      // Resend the invitation using the dedicated resend endpoint
      const headers = await getAuthHeaders();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/trip/resend-invite`, {
         method: 'POST',
         headers,
         body: JSON.stringify({
            invitationId: invitationId,
         }),
      });

      if (!response.ok) {
         const errorData = await response.json();
         throw new Error(errorData.error || 'Failed to resend invitation');
      }

      const result = await response.json();
      console.log('Trip invitation resent successfully:', result);
   } catch (error) {
      console.error('Error resending trip invitation:', error);
      throw error;
   }
};
